import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  ButtonComponent,
  EmptyPlaceholderActionsDirective,
  EmptyPlaceholderComponent,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { faSearch, faUser } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { PlatformFollowUpChoiceModalComponent } from '../../../../components/follow-up-choice-modal/platform-follow-up-choice-modal.component';
import { PlatformApplication } from '../../../../models/platform.models';
import { PlatformDashboardService } from '../../../../services/platform-dashboard.service';
import { PlatformJobsService } from '../../../../services/platform-jobs.service';
import { PlatformJobsCompleteModalComponent } from '../../../jobs/components/jobs-complete-modal/platform-jobs-complete-modal.component';
import { PlatformDashboardApplicationsComponent } from '../../components/dashboard-applications/platform-dashboard-applications.component';
import { PlatformDashboardInvitationsComponent } from '../../components/dashboard-invitations/platform-dashboard-invitations.component';
import { PlatformDashboardProfileCompletenessComponent } from '../../components/dashboard-profile-completeness/platform-dashboard-profile-completeness.component';
import { PlatformDashboardTasksComponent } from '../../components/dashboard-tasks/platform-dashboard-tasks.component';
import { viewLoader } from '../../../../../../assets/animations/animations';

@Component({
  selector: 'app-platform-dashboard-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    ButtonComponent,
    EmptyPlaceholderActionsDirective,
    EmptyPlaceholderComponent,
    TranslocoPipe,
    PlatformDashboardTasksComponent,
    PlatformDashboardInvitationsComponent,
    PlatformDashboardApplicationsComponent,
    PlatformDashboardProfileCompletenessComponent,
    PlatformFollowUpChoiceModalComponent,
    PlatformJobsCompleteModalComponent,
  ],
  templateUrl: './platform-dashboard-view.component.html',
  styleUrl: './platform-dashboard-view.component.scss',
  animations: [viewLoader],
})
export class PlatformDashboardViewComponent {
  readonly dashboard = inject(PlatformDashboardService);
  private readonly jobs = inject(PlatformJobsService);
  private readonly toastsService = inject(ToastsService);
  readonly icons = {
    user: faUser,
    search: faSearch,
  };

  readonly completingApplication = signal<PlatformApplication | null>(null);

  private readonly choiceModal =
    viewChild.required<PlatformFollowUpChoiceModalComponent>('choiceModal');
  private readonly completeModal =
    viewChild.required<PlatformJobsCompleteModalComponent>('completeModal');

  /**
   * Opens the follow-up choice modal for an action item.
   * @param applicationId - Tracked application id
   */
  openFollowUpChoice(applicationId: number): void {
    const application = this.jobs
      .applications()
      .find((item) => item.id === applicationId);
    if (!application) {
      return;
    }
    this.choiceModal().open(application);
  }

  /**
   * Opens the manual follow-up modal for an application.
   * @param applicationId - Tracked application id
   */
  openCompleteModal(applicationId: number): void {
    const application = this.jobs
      .applications()
      .find((item) => item.id === applicationId);
    if (!application) {
      return;
    }
    this.completingApplication.set(application);
    queueMicrotask(() => this.completeModal().open());
  }

  /**
   * Shows a success toast after follow-up data was saved.
   */
  onCompleted(): void {
    this.toastsService.success(
      'jobboard.platform.jobs.applications.complete-toast'
    );
    this.completingApplication.set(null);
  }
}
